import { Badge } from './ui.jsx';
import { currency, cx, rate } from '../lib/format.js';

/**
 * EmiPlanSelector — the selectable list of EMI plans.
 *
 * Accessibility note: this is a genuine single-choice control, so it is built as
 * a real radio group with native `<input type="radio">` elements visually hidden
 * behind styled labels. That gives keyboard arrow-key navigation, correct screen
 * reader semantics and form-level validation for free — none of which a
 * `<div onClick>` would provide.
 */

function CheckIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
      <path d="m5 13 4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlanRow({ plan, isSelected, onSelect, name }) {
  const inputId = `emi-plan-${plan.tenureMonths}`;

  return (
    <label
      htmlFor={inputId}
      className={cx(
        'relative flex cursor-pointer items-center gap-4 rounded-xl border p-4 transition-all',
        isSelected
          ? 'border-brand-500 bg-brand-50/70 ring-1 ring-brand-500'
          : 'border-ink-200 bg-white hover:border-brand-300 hover:bg-brand-50/30',
      )}
    >
      <input
        id={inputId}
        type="radio"
        name={name}
        value={plan.tenureMonths}
        checked={isSelected}
        onChange={() => onSelect(plan)}
        className="sr-only"
      />

      {/* Custom radio indicator. aria-hidden because the real input conveys state. */}
      <span
        className={cx(
          'grid size-5 shrink-0 place-items-center rounded-full border-2 transition-colors',
          isSelected ? 'border-brand-600 bg-brand-600 text-white' : 'border-ink-300 bg-white',
        )}
        aria-hidden="true"
      >
        {isSelected && <CheckIcon className="size-3" />}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-base font-bold text-ink-950">
            {currency(plan.monthlyAmount)}
            <span className="text-sm font-medium text-ink-500">/mo</span>
          </span>
          <span className="text-sm text-ink-600">× {plan.tenureMonths} months</span>
        </span>

        <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          {plan.isNoCostEmi ? (
            <span className="font-semibold text-growth-700">0% interest · no cost EMI</span>
          ) : (
            <span className="text-ink-500">{rate(plan.interestRate)} p.a. interest</span>
          )}

          {plan.cashback > 0 && (
            <span className="font-semibold text-growth-700">{currency(plan.cashback)} cashback</span>
          )}

          {plan.totalInterest > 0 && (
            <span className="text-ink-500">Total {currency(plan.totalPayable)}</span>
          )}
        </span>
      </span>

      {plan.badge && (
        <Badge tone={plan.isNoCostEmi ? 'growth' : 'brand'} className="shrink-0 self-start">
          {plan.badge}
        </Badge>
      )}
    </label>
  );
}

export default function EmiPlanSelector({ plans, selectedPlan, onSelect, id = 'emi-plans' }) {
  if (!plans?.length) {
    return (
      <p className="rounded-xl bg-ink-100 p-4 text-sm text-ink-600">
        No EMI plans are configured for this variant yet.
      </p>
    );
  }

  return (
    <fieldset id={id}>
      <legend className="sr-only">Choose an EMI plan</legend>
      <div className="space-y-2.5" role="radiogroup" aria-label="EMI plans">
        {plans.map((plan) => (
          <PlanRow
            key={plan.id ?? plan.tenureMonths}
            plan={plan}
            name={id}
            isSelected={selectedPlan?.tenureMonths === plan.tenureMonths}
            onSelect={onSelect}
          />
        ))}
      </div>
    </fieldset>
  );
}
